import { useState, useCallback } from 'react'
import { AnimatePresence } from 'framer-motion'
import { SparklesIcon, SearchIcon, ArrowLeft } from 'lucide-react'
import { useTranslations } from 'next-intl'
import type { Asset, AssetType } from '@/lib/types'
import useDebounce from '@/hooks/use-debounce'
import { useProjectStore } from '@/hooks/use-project-store'
import { useFlyingAnimation } from '@/hooks/use-flying-animation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { FlyingImageAnimation } from '@/components/common/flying-image-animation'
import { GradientButton } from '@/components/common/gradient-button'
import { AIGeneratorPopup } from '@/components/ai-generator-popup'
import {
  CreationLibrary,
  CreationLoadingProvider,
  useCreationLoading,
} from './creation-library'
import { useSWRConfig } from 'swr'
import { ImageLibrary } from './image-library'
import { VideoLibrary } from './video-library'
import { GraphicLibrary } from './graphic-library'
import { GifLibrary } from './gif-library'
import { StickerLibrary } from './sticker-library'
import { AudioLibrary } from './audio-library'
import { useCurrentUser } from '@/hooks/use-current-user'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import { IconLibrary } from './icon-library'

// Keep the type definition but use it only for type checking and iteration
const assetTypes: AssetType[] = [
  'creation',
  'image',
  'video',
  'graphic',
  'gif',
  'pdf',
  'sticker',
  'audio',
  'icon',
]

export const ElementLibrary = () => {
  return (
    <CreationLoadingProvider>
      <ElementLibraryContent />
    </CreationLoadingProvider>
  )
}

const ElementLibraryContent = () => {
  const t = useTranslations('element-library')
  const {
    addSelectedAsset,
    selectedAssets,
    removeSelectedAsset,
    elementSearchQuery,
    setElementSearchQuery,
    setChatBarHighlighting,
    project,
  } = useProjectStore()
  const { data: user } = useCurrentUser()
  const [expandedType, setExpandedType] = useState<AssetType | null>(null)
  const [showAIGenerator, setShowAIGenerator] = useState(false)
  const debouncedSearch = useDebounce(elementSearchQuery, 500)
  const { setLoadingCount, setIsGenerating } = useCreationLoading()
  const { mutate } = useSWRConfig()

  // Store mutate function from CreationLibrary for direct cache refresh
  const [creationMutate, setCreationMutate] = useState<(() => void) | null>(
    null,
  )

  // Callback to receive mutate function from CreationLibrary
  const handleMutateReady = useCallback((mutateFunction: () => void) => {
    console.log(
      'CreationLibrary mutate function received:',
      typeof mutateFunction,
    )
    setCreationMutate(() => mutateFunction)
  }, [])

  const chatTabPosition = {
    x: 28,
    y: window.innerHeight - 128,
  }

  const { selectedImageUrl, clickPosition, handleSelect, resetAnimation } =
    useFlyingAnimation()

  const handleAssetSelect = (e: React.MouseEvent, asset: Asset) => {
    if (selectedAssets.some((a) => a.link === asset.link)) {
      removeSelectedAsset(asset)
      return
    }

    if (e) {
      handleSelect(e, asset.link)
      setChatBarHighlighting(true)
    }

    addSelectedAsset(asset)

    setTimeout(() => {
      resetAnimation()
      setChatBarHighlighting(false)
    }, 500)

    // add mixpanel event
    sendToBothAnalytics(
      MIXPANEL_CLIENT_EVENT_NAMES.CLICK_ELEMENT_LIBRARY_ASSET,
      { project_id: project?._id.toString(), asset_id: asset.link },
      user?._id.toString() || '',
    )
  }

  const renderAssetSection = (type: AssetType) => {
    const Component = {
      creation: CreationLibrary,
      image: ImageLibrary,
      video: VideoLibrary,
      graphic: GraphicLibrary,
      gif: GifLibrary,
      pdf: null,
      csv: null,
      table: null,
      sticker: StickerLibrary,
      product: ImageLibrary, // TODO: Add product library
      audio: AudioLibrary,
      icon: IconLibrary,
      attachment: null,
      app: null,
      super_prompt: null,
      api_credentials_request: null,
    }[type]

    if (!Component) return null

    const isExpanded = expandedType === type

    return (
      <div key={type}>
        <div className="relative mb-4">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold">
              {t(`asset_types.${type}`)}
            </h2>
            {(type === 'gif' || type === 'sticker') && (
              <img
                src="https://heyboss.heeyo.ai/klipy-logo.jpg"
                alt="Klipy"
                className="h-4"
              />
            )}
          </div>
          <div className="absolute right-0 top-0">
            {isExpanded ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpandedType(null)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('actions.back')}
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpandedType(type)}
              >
                {t('actions.see_all')}
              </Button>
            )}
          </div>
        </div>
        <div
          className={
            isExpanded
              ? expandedType === 'icon'
                ? 'grid grid-cols-4 gap-2'
                : expandedType === 'creation'
                  ? 'w-full'
                  : 'grid grid-cols-1 sm:grid-cols-2 gap-4'
              : 'relative'
          }
        >
          {isExpanded ? (
            <Component
              query={debouncedSearch}
              selectedAssets={selectedAssets}
              onSelect={handleAssetSelect}
              isExpanded
              {...(type === 'creation' && {
                columns: 4,
                onMutateReady: handleMutateReady,
              })}
            />
          ) : (
            <ScrollArea className="max-w-96" type="scroll">
              <div className="flex gap-4 pb-4 min-w-full w-max">
                <Component
                  query={debouncedSearch}
                  selectedAssets={selectedAssets}
                  onSelect={handleAssetSelect}
                  {...(type === 'creation' && {
                    onMutateReady: handleMutateReady,
                  })}
                />
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {expandedType === 'creation' ? (
        // Full width layout for CreationLibrary
        <>
          <div className="p-4">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-white/60" />
              <Input
                type="text"
                placeholder={t('search.placeholder')}
                value={elementSearchQuery}
                onChange={(e) => setElementSearchQuery(e.target.value)}
                className="pl-9 bg-white/5 border-white/10 dark:bg-white/5 dark:border-white/10 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/60"
              />
            </div>
          </div>
          <div className="mx-4 h-px bg-white/10" />
          <ScrollArea className="flex-1 my-4 px-4">
            {renderAssetSection(expandedType)}
          </ScrollArea>
        </>
      ) : (
        // Original layout for other types
        <>
          <div className="p-4">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-white/60" />
              <Input
                type="text"
                placeholder={
                  expandedType === 'gif' || expandedType === 'sticker'
                    ? t('search.klipy_placeholder')
                    : t('search.placeholder')
                }
                value={elementSearchQuery}
                onChange={(e) => setElementSearchQuery(e.target.value)}
                className="pl-9 bg-white/5 border-white/10 dark:bg-white/5 dark:border-white/10 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/60"
              />
            </div>
          </div>
          <div className="mx-4 h-px bg-white/10" />
          <div className="p-4">
            <div className="relative">
              <GradientButton
                className="w-full"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowAIGenerator(true)
                  // add mixpanel event
                  sendToBothAnalytics(
                    MIXPANEL_CLIENT_EVENT_NAMES.CLICK_ELEMENT_LIBRARY_AI_GENERATE,
                    { project_id: project?._id.toString() },
                    user?._id.toString() || '',
                  )
                }}
              >
                <SparklesIcon className="w-4 h-4 mr-2" />
                {t('ai_generate.title')}
              </GradientButton>
            </div>
          </div>
          <div className="mx-4 h-px bg-white/10" />
          <ScrollArea className="flex-1 my-4">
            <div className="px-4">
              {expandedType
                ? renderAssetSection(expandedType)
                : assetTypes.map((type) => renderAssetSection(type))}
            </div>
          </ScrollArea>
        </>
      )}

      <AnimatePresence>
        <FlyingImageAnimation
          isVisible={!!(selectedImageUrl && clickPosition)}
          sourcePosition={clickPosition || { x: 0, y: 0 }}
          targetPosition={chatTabPosition}
          imageUrl={selectedImageUrl || ''}
        />
      </AnimatePresence>

      {/* AI Generator Popup */}
      {showAIGenerator && (
        <AIGeneratorPopup
          size="large"
          onImageSelect={(imageUrl: string) => {
            const asset: Asset = {
              source: 'creation',
              type: 'image',
              link: imageUrl,
              thumbnail: imageUrl,
            }
            addSelectedAsset(asset)
            setShowAIGenerator(false)
            setChatBarHighlighting(true)
            setTimeout(() => {
              resetAnimation()
              setChatBarHighlighting(false)
            }, 500)
          }}
          onClose={() => {
            setShowAIGenerator(false)
          }}
          onGenerationStart={(count: number) => {
            setLoadingCount(count)
            setIsGenerating(true)

            // End loading after 2 seconds and trigger refresh
            setTimeout(() => {
              // Refresh cache when loading ends
              if (creationMutate && typeof creationMutate === 'function') {
                console.log('Refreshing CreationLibrary cache')
                creationMutate()
              } else {
                console.log('Using global SWR mutate for refresh')
                mutate(
                  (key) =>
                    typeof key === 'string' &&
                    key.startsWith('/api/me/assets?source=creation'),
                  undefined,
                  { revalidate: true },
                )
              }

              // End loading state
              setLoadingCount(0)
              setIsGenerating(false)
            }, 2000) // 2 seconds of loading feedback
          }}
          onGenerationEnd={() => {
            setLoadingCount(0)
            setIsGenerating(false)
          }}
        />
      )}
    </div>
  )
}
